import React, { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";

import CloseIcon from "@mui/icons-material/Close";
import { Box, BoxProps, Button, CircularProgress, IconButton, Modal, Stack, Typography } from "@mui/material";
import ReachedLimitImg from "assets/images/ReachedLimit.png";
import { useApi } from "components/providers/Api-provider/ApiContext";
import { useDevice } from "hooks";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  manualUpgrade?: boolean;
}

const style: BoxProps["sx"] = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  outline: "none",
  borderRadius: "8px",
  overflow: "hidden",
  bgcolor: "white",
  boxShadow: "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
  p: "32px",
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  width: "100%",
  maxWidth: "520px"
};

export const UpgradePlanModal: React.FC<Props> = ({ isOpen, onClose, manualUpgrade }) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const { altiusApi } = useApi();
  const { isMobile } = useDevice();

  const handleUpgrade = useCallback(async () => {
    try {
      setLoading(true);
      // Send upgrade request to backend directly using API
      await altiusApi.post({
        url: "api/v0.0.3/profile/request",
        data: { message: "Upgrade request", type: "upgrade-freemium-account" }
      });
      toast("Upgrade request sent to Altius");
      setStep(2);
    } catch (error) {
      console.error("Failed to send upgrade request:", error);
      // On error, we could show an error message or keep on step 1
    } finally {
      setLoading(false);
    }
  }, [altiusApi]);

  const handleClose = () => {
    setStep(1);
    onClose();
  };

  const headerText = useMemo(() => {
    if (manualUpgrade) {
      return "Upgrade";
    }
    if (step === 1) {
      return "You've Reached Your Free Plan Limit";
    }
    return "Request Received";
  }, [manualUpgrade, step]);

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box sx={{ ...style, ...(isMobile && { maxWidth: "95%" }) }}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            borderBottom: "1px solid #dbe8f5",
            pb: "8px",
            gap: "6px"
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontSize: "18px",
              fontWeight: 400,
              lineHeight: "24px",
              color: "#071f35"
            }}
          >
            {headerText}
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{
              p: "4px",
              borderRadius: "32px",
              "&:hover": {
                bgcolor: "transparent"
              }
            }}
          >
            <CloseIcon sx={{ fontSize: "16px", color: "#071f35" }} />
          </IconButton>
        </Stack>

        {/* Content */}
        {step === 1 ? (
          <>
            <Box
              component="img"
              src={ReachedLimitImg as string}
              alt="Reached limit"
              sx={{
                width: "100%",
                maxWidth: "346px",
                height: "auto",
                display: "block",
                mx: "auto"
              }}
            />
            <Box sx={{ width: "100%" }}>
              {!manualUpgrade && (
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    color: "#071f35",
                    mb: "8px"
                  }}
                >
                  You&apos;ve gotten a taste of what Altius can do. To keep managing deals, syncing funds, and
                  extracting insights without interruption, upgrade to a full plan and pick up right where you left off.
                </Typography>
              )}
              {!!manualUpgrade && (
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 700,
                    lineHeight: "24px",
                    color: "#071f35"
                  }}
                >
                  Upgrading unlocks unlimited usage, faster responses, and access to advanced features — so you can keep
                  your workflow moving.
                </Typography>
              )}
            </Box>

            {/* Action Buttons */}
            <Stack direction="row" gap="8px" sx={{ justifyContent: "flex-end", width: "100%" }}>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={loading}
                sx={{
                  bgcolor: "white",
                  borderColor: "#7b92a9",
                  color: "#071f35",
                  borderRadius: "32px",
                  px: "16px",
                  py: "4px",
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: "24px",
                  textTransform: "none",
                  maxWidth: "240px",
                  "&:hover": {
                    bgcolor: "white",
                    borderColor: "#7b92a9"
                  }
                }}
              >
                Maybe Later
              </Button>
              <Button
                variant="contained"
                onClick={handleUpgrade}
                disabled={loading}
                sx={{
                  bgcolor: "#071f35",
                  color: "white",
                  borderRadius: "32px",
                  px: "16px",
                  py: "4px",
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: "24px",
                  textTransform: "none",
                  maxWidth: "240px",
                  "&:hover": {
                    bgcolor: "#071f35"
                  }
                }}
              >
                {loading ? <CircularProgress size={16} color="inherit" /> : "Request Upgrade"}
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <Box sx={{ width: "100%" }}>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: "24px",
                  color: "#071f35",
                  mb: "8px"
                }}
              >
                Thanks for your interest in upgrading.
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: "24px",
                  color: "#071f35"
                }}
              >
                Our team will contact you as soon as possible to complete the process and help you switch to the paid
                plan.
              </Typography>
            </Box>

            {/* Action Button */}
            <Stack direction="row" gap="8px" sx={{ justifyContent: "flex-end", width: "100%" }}>
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  bgcolor: "white",
                  borderColor: "#7b92a9",
                  color: "#071f35",
                  borderRadius: "32px",
                  px: "16px",
                  py: "4px",
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: "24px",
                  textTransform: "none",
                  maxWidth: "240px",
                  "&:hover": {
                    bgcolor: "white",
                    borderColor: "#7b92a9"
                  }
                }}
              >
                Close
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </Modal>
  );
};
